import type { MenuItem, Recipe } from './types';

/**
 * Donde se usa una receta (mejora 50 de Alex, 5 sep 2026): como subreceta
 * de otras recetas o como plato de la carta. Es UNA logica para la API, la
 * demo y el frontend, para que el mensaje de «no se puede borrar» sea el
 * mismo en los tres sitios.
 */
export interface RecipeUsage {
  /** Recetas que la llevan como subreceta. */
  recetas: Recipe[];
  /** Platos de la carta enlazados a ella. */
  platos: MenuItem[];
}

export function recipeUsage(
  recipeId: string,
  recipes: readonly Recipe[],
  menuItems: readonly MenuItem[],
): RecipeUsage {
  return {
    recetas: recipes.filter(
      (r) =>
        r.recipeId !== recipeId &&
        r.components.some((c) => c.ref.kind === 'recipe' && c.ref.recipeId === recipeId),
    ),
    platos: menuItems.filter((m) => m.recipeId === recipeId),
  };
}

const lista = (nombres: string[]): string => {
  const tres = nombres.slice(0, 3).map((n) => `«${n}»`).join(', ');
  return nombres.length > 3 ? `${tres} y ${nombres.length - 3} más` : tres;
};

/** Mensaje para el usuario, o null si la receta se puede borrar. */
export function recipeUsageMessage(uso: RecipeUsage): string | null {
  const partes: string[] = [];
  if (uso.recetas.length > 0) {
    partes.push(
      `se utiliza como subreceta en ${uso.recetas.length === 1 ? 'la receta' : `${uso.recetas.length} recetas`}: ${lista(uso.recetas.map((r) => r.name))}`,
    );
  }
  if (uso.platos.length > 0) {
    partes.push(
      `está en la carta como ${uso.platos.length === 1 ? 'el plato' : `${uso.platos.length} platos`}: ${lista(uso.platos.map((m) => m.name))}`,
    );
  }
  if (partes.length === 0) return null;
  return `No puedes eliminar esta receta porque ${partes.join(' y ')}. Quítala de ahí primero.`;
}

/** Recetas que llevan un ingrediente: para no dejar recetas con «un ingrediente que ya no existe». */
export function ingredientUsage(ingredientId: string, recipes: readonly Recipe[]): Recipe[] {
  return recipes.filter((r) =>
    r.components.some((c) => c.ref.kind === 'ingredient' && c.ref.ingredientId === ingredientId),
  );
}

export function ingredientUsageMessage(recetas: readonly Recipe[]): string | null {
  if (recetas.length === 0) return null;
  const donde = recetas.length === 1 ? 'la receta' : `${recetas.length} recetas`;
  return `No puedes eliminar este ingrediente porque se usa en ${donde}: ${lista(recetas.map((r) => r.name))}. Quítalo de ahí primero.`;
}
